import { Button, Flex, Text } from '@mantine/core';
import { useState } from 'react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { FileUploadInput, UploadedFile } from 'src/data-capture/hooks/file-uploads/types';
import { useFileUploadActions } from 'src/data-capture/hooks/file-uploads/useFileUploadActions';
import { useFileUploads } from 'src/data-capture/hooks/file-uploads/useFileUploads';
import SelectableItemList from 'src/UI/drawer-flow/SelectableItemList';
import { ReportData } from '../SuitabilityFlow';
import { StepProps } from 'src/UI/drawer-flow/DrawerFlow';
import DrawerFlowScreenLayout from 'src/UI/drawer-flow/DrawerFlowScreenLayout';
import pluralize from 'pluralize';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { useMeetingNotes } from '../hooks/useMeetingNotes';

interface SelectFilesStepProps extends StepProps<ReportData> {
  title: string;
  description: string;
  hint?: string;
  selectedFileIds: string[];
  setSelectedFileIds: (fileIds: string[]) => void;
  filterFiles?: (files: UploadedFile[]) => UploadedFile[];
  footer?: React.ReactNode;
  includeMeetingNotes?: boolean;
}

export default function SelectFilesStep(props: SelectFilesStepProps) {
  const { selectedFileIds, setSelectedFileIds, data } = props;
  const filter = data.family?.id ? { familyId: data.family!.id } : { clientId: data.client?.id };
  const { files, isLoading: isLoadingFiles } = useFileUploads(filter);

  const { meetingNotes, isLoading: isLoadingMeetingNotes } = useMeetingNotes({
    clientId: data.client?.id,
    enabled: props.includeMeetingNotes,
  });

  const isLoading = isLoadingFiles || (props.includeMeetingNotes && isLoadingMeetingNotes);

  const filteredFiles = props.filterFiles ? props.filterFiles(files) : files;

  const fileItems = filteredFiles.map(file => ({
    id: file.id,
    title: file.file.fileName,
    description: `Uploaded: ${new Date(file.file.createdAt).toLocaleDateString()}`,
  }));

  const items = props.includeMeetingNotes
    ? [
        ...fileItems,
        ...meetingNotes
          .filter(note => note.backgroundProcess?.status === 'SUCCESS')
          .map(note => ({
            id: note.id,
            title: note.fileName ?? 'Meeting Note',
            description: `Generated: ${new Date(note.createdAt).toLocaleDateString()}`,
          })),
      ]
    : fileItems;
  const { uploadFiles } = useFileUploadActions({ clientId: data.client?.id });
  const [isUploading, setIsUploading] = useState(false);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files;

    if (fileList) {
      setIsUploading(true);
      try {
        const payload: FileUploadInput[] = Array.from(fileList).map(file => ({
          file,
          isResolved: true,
        }));
        const result = await uploadFiles(payload);
        const newUploadedFileIds: string[] = result.map(response => response.data.id);
        setSelectedFileIds([...selectedFileIds, ...newUploadedFileIds]);
      } catch (error) {
        notifyAndLogError(
          'Failed to upload files',
          'An error occurred while uploading files. Please try again.',
          error,
        );
      }
      setIsUploading(false);
    }

    event.target.value = '';
  };

  return (
    <DrawerFlowScreenLayout
      {...props}
      title={props.title}
      description={props.description}
      isLoading={isLoading}
      showClientName={false}
    >
      <Flex direction="column" gap="xs" mih={0} flex={1} h="100%">
        <Flex justify="space-between" align="center" style={{ flexShrink: 0 }}>
          <Text size="textSm" fw={FONT_WEIGHTS.medium} c="black" data-testid="document-count">
            {items.length} {pluralize('document', items.length)} found
          </Text>
          <Button
            component="label"
            variant="subtle"
            loading={isUploading}
            style={{ cursor: 'pointer' }}
          >
            Upload
            <input type="file" multiple style={{ display: 'none' }} onChange={handleFileUpload} />
          </Button>
        </Flex>
        <SelectableItemList
          items={items}
          selectedItemIds={selectedFileIds}
          setSelectedItemIds={setSelectedFileIds}
          hint={props.hint}
          selectMultiple
        />
      </Flex>
    </DrawerFlowScreenLayout>
  );
}
